import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Instagram, Settings, Link2, Unlink, CheckCircle2, AlertCircle,
  ExternalLink, Copy, ChevronRight, Loader2, HelpCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const InstagramIntegrationPage = () => {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [setupInstructions, setSetupInstructions] = useState(null);

  useEffect(() => {
    loadAccount();
    checkUrlParams();
  }, []);

  const checkUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const error = params.get('error');
    const username = params.get('username');

    if (connected === 'true') {
      toast.success(`Instagram account @${username || ''} connected successfully!`);
      window.history.replaceState({}, '', '/integrations/instagram');
      loadAccount();
    } else if (error) {
      toast.error(`Connection failed: ${error}`);
      window.history.replaceState({}, '', '/integrations/instagram');
    }
  };

  const loadAccount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/instagram/account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccount(response.data.account);
    } catch (error) {
      console.error('Failed to load Instagram account:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSetupInstructions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/instagram/setup-instructions`);
      setSetupInstructions(response.data);
      setSetupDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load setup instructions');
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/instagram/auth/url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Facebook OAuth
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Failed to initiate Instagram connection:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to initiate connection';
      toast.error(errorMsg);
      
      if (errorMsg.includes('not configured')) {
        setSetupDialogOpen(true);
        loadSetupInstructions();
      }
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Instagram account?')) {
      return;
    }

    setDisconnecting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/instagram/account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccount(null);
      toast.success('Instagram account disconnected');
    } catch (error) {
      toast.error('Failed to disconnect account');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-wt-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wt-bg p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
              <Instagram className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-wt-text-primary">Instagram Integration</h1>
              <p className="text-wt-text-muted">Connect your Instagram Business account</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={loadSetupInstructions}
            className="border-wt-border text-wt-text-secondary hover:text-wt-text-primary"
          >
            <HelpCircle className="h-4 w-4 mr-2" />
            Setup Guide
          </Button>
        </motion.div>

        {/* Connection Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-wt-surface border-wt-border">
            <CardHeader>
              <CardTitle className="text-wt-text-primary flex items-center gap-2">
                <Link2 className="h-5 w-5" />
                Connection Status
              </CardTitle>
              <CardDescription className="text-wt-text-muted">
                Link your Instagram Professional account to enable messaging and posting
              </CardDescription>
            </CardHeader>
            <CardContent>
              {account ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                    <CheckCircle2 className="h-8 w-8 text-green-400" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold text-wt-text-primary">
                          @{account.instagram_username}
                        </span>
                        <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">
                          Connected
                        </Badge>
                      </div>
                      <p className="text-sm text-wt-text-muted">
                        {account.instagram_name || account.page_name}
                        {account.followers_count > 0 && ` • ${account.followers_count.toLocaleString()} followers`}
                      </p>
                    </div>
                    {account.profile_picture_url && (
                      <img 
                        src={account.profile_picture_url} 
                        alt="Profile" 
                        className="h-12 w-12 rounded-full border-2 border-wt-border"
                      />
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="p-3 bg-wt-bg rounded-lg">
                      <p className="text-wt-text-muted">Facebook Page</p>
                      <p className="text-wt-text-primary font-medium">{account.page_name}</p>
                    </div>
                    <div className="p-3 bg-wt-bg rounded-lg">
                      <p className="text-wt-text-muted">Connected On</p>
                      <p className="text-wt-text-primary font-medium">
                        {new Date(account.connected_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <Button
                    variant="destructive"
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                    className="w-full"
                  >
                    {disconnecting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Unlink className="h-4 w-4 mr-2" />
                    )}
                    Disconnect Instagram
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Alert className="bg-yellow-500/10 border-yellow-500/30">
                    <AlertCircle className="h-4 w-4 text-yellow-400" />
                    <AlertDescription className="text-wt-text-secondary">
                      Connect your Instagram Business account to enable messaging, chatbot automation, and posting.
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2">
                    <h4 className="font-medium text-wt-text-primary">Before connecting, ensure you have:</h4>
                    <ul className="space-y-1 text-sm text-wt-text-muted">
                      <li className="flex items-center gap-2">
                        <ChevronRight className="h-4 w-4 text-wt-gold" />
                        Instagram Professional account (Business or Creator)
                      </li>
                      <li className="flex items-center gap-2">
                        <ChevronRight className="h-4 w-4 text-wt-gold" />
                        Facebook Business Page connected to your Instagram
                      </li>
                      <li className="flex items-center gap-2">
                        <ChevronRight className="h-4 w-4 text-wt-gold" />
                        Admin access to the Facebook Page
                      </li>
                    </ul>
                  </div>

                  <Button
                    onClick={handleConnect}
                    disabled={connecting}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                  >
                    {connecting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Instagram className="h-4 w-4 mr-2" />
                    )}
                    Connect Instagram Account
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Features Cards */}
        {account && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            <Card 
              className="bg-wt-surface border-wt-border cursor-pointer hover:border-wt-gold/50 transition-colors"
              onClick={() => window.location.href = '/marketing/inbox'}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <Instagram className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-wt-text-primary">Instagram Inbox</h3>
                    <p className="text-xs text-wt-text-muted">View and reply to DMs</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-wt-text-muted ml-auto" />
                </div>
              </CardContent>
            </Card>

            <Card 
              className="bg-wt-surface border-wt-border cursor-pointer hover:border-wt-gold/50 transition-colors"
              onClick={() => window.location.href = '/marketing/instagram/chatbot'}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <Settings className="h-5 w-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-wt-text-primary">Instagram Chatbot</h3>
                    <p className="text-xs text-wt-text-muted">Automated responses</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-wt-text-muted ml-auto" />
                </div>
              </CardContent>
            </Card>

            <Card 
              className="bg-wt-surface border-wt-border cursor-pointer hover:border-wt-gold/50 transition-colors"
              onClick={() => window.location.href = '/marketing/instagram/post'}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-pink-500/20 rounded-lg">
                    <ExternalLink className="h-5 w-5 text-pink-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-wt-text-primary">Post to Instagram</h3>
                    <p className="text-xs text-wt-text-muted">Create posts & auto-post</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-wt-text-muted ml-auto" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Setup Instructions Dialog */}
        <Dialog open={setupDialogOpen} onOpenChange={setSetupDialogOpen}>
          <DialogContent className="bg-wt-surface border-wt-border max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-wt-text-primary flex items-center gap-2">
                <Instagram className="h-5 w-5" />
                Instagram API Setup Guide
              </DialogTitle>
              <DialogDescription className="text-wt-text-muted">
                Follow these steps to configure Instagram integration
              </DialogDescription>
            </DialogHeader>

            {setupInstructions && (
              <div className="space-y-6 mt-4">
                {/* Steps */}
                <div className="space-y-4">
                  {setupInstructions.steps.map((step, index) => (
                    <div key={index} className="flex gap-4 p-4 bg-wt-bg rounded-lg">
                      <div className="flex-shrink-0 w-8 h-8 bg-wt-gold/20 rounded-full flex items-center justify-center">
                        <span className="text-wt-gold font-bold">{step.step}</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-wt-text-primary">{step.title}</h4>
                        <p className="text-sm text-wt-text-muted mt-1">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Environment Variables */}
                <div className="p-4 bg-wt-bg rounded-lg">
                  <h4 className="font-medium text-wt-text-primary mb-3">Required Environment Variables</h4>
                  <div className="space-y-2">
                    {setupInstructions.environment_variables.map((env, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-wt-surface rounded border border-wt-border">
                        <div>
                          <code className="text-sm text-wt-gold">{env.name}</code>
                          <p className="text-xs text-wt-text-muted">{env.description}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            navigator.clipboard.writeText(env.name);
                            toast.success('Copied to clipboard');
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Permissions */}
                <div className="p-4 bg-wt-bg rounded-lg">
                  <h4 className="font-medium text-wt-text-primary mb-3">Required Permissions</h4>
                  <div className="flex flex-wrap gap-2">
                    {setupInstructions.permissions_needed.map((perm, index) => (
                      <Badge key={index} variant="outline" className="bg-wt-surface border-wt-border text-wt-text-secondary">
                        {perm}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default InstagramIntegrationPage;
